import { useState } from "react";
import { useForm, type RegisterOptions, type SubmitHandler } from "react-hook-form";
import { type Point } from "./../types";

type PointFormInput = Omit<Point, "id">;

const NumberInputRules: RegisterOptions<PointFormInput, "x" | "y" | "z"> = {
  required: "Enter a coordinate.",
  valueAsNumber: true,
  validate: (value: number) => Number.isFinite(value) || "Enter a finite number.",
};

function PointForm ({
  points,
  onSubmit,
  onRemove,
}: {
  points: Point[],
  onSubmit: (point: Point) => void,
  onRemove: (id: string) => void
}) {
  const [status, setStatus] = useState("Ready to add a point.");
  const { handleSubmit, register, reset, formState } = useForm<PointFormInput>()
  const onSubmitPoint: SubmitHandler<PointFormInput> = (data) => {
    const newPoint = {
      ...data,
      id: self.crypto.randomUUID()
    };

    onSubmit(newPoint);
    setStatus(`Point ${data.name} added.`);
    reset();
  };

  const onInvalidPoint = () => {
    setStatus("Enter a name and finite numeric coordinates.");
  };

  return (
    <div className="points-container">
      <ul>
        { points.map((p) => (
          <li key={p.id}>
          {p.name}({p.x};{p.y};{p.z})
          <button onClick={() => onRemove(p.id)}>Remove</button>
          </li>
        ))}
      </ul>
      <hr />
      <form onSubmit={handleSubmit(onSubmitPoint, onInvalidPoint)}>
        <p className="form-status" role="status">{status}</p>
        <label>
          Name:
          <input
            aria-invalid={Boolean(formState.errors.name)}
            className={formState.errors.name ? "is-invalid" : undefined}
            {...register("name", { required: true })}
          />
        </label>
        <label>
          X:
          <input
            aria-invalid={Boolean(formState.errors.x)}
            className={formState.errors.x ? "is-invalid" : undefined}
            {...register("x", NumberInputRules)}
          />
        </label>
        <label>
          Y:
          <input
            aria-invalid={Boolean(formState.errors.y)}
            className={formState.errors.y ? "is-invalid" : undefined}
            {...register("y", NumberInputRules)}
          />
        </label>
        <label>
          Z:
          <input
            aria-invalid={Boolean(formState.errors.z)}
            className={formState.errors.z ? "is-invalid" : undefined}
            {...register("z", NumberInputRules)}
          />
        </label>
        <input type="submit" value="Add point" />
      </form>
    </div>
  );
}

export default PointForm;
